// Review usage documentation in the README.md file in this directory
import { LoggingHandler } from 'root/utils/errorReporting/LoggingHandler';

declare global {
    interface Window {
        dataLayer?: Record<string, unknown>[];
        google_tag_manager?: Record<string, unknown>;
    }
}

type incompleteGTMEvent = {
    [key: string]: string | number | string[] | undefined;
} & { event?: never };

type GTMEvent = incompleteGTMEvent & {
    event_action: string;
};

type TagCompletionWait = {
    minimumMilliseconds: number;
    maximumMilliseconds: number;
};

// The parts of a window.google_tag_manager[containerId] entry read here
type GoogleTagManagerContainer = {
    dataLayer?: { name?: string };
};

// gtag.js destinations (AW-, G-, DC-) share the dataLayer and call eventCallback without waiting
const GTM_CONTAINER_ID_PREFIX = 'GTM-';
// A container loaded with another dataLayer name never sees these pushes, so never calls back
const DATA_LAYER_NAME = 'dataLayer';

class GoogleTagManager {
    #eventObject: incompleteGTMEvent;

    constructor(event: incompleteGTMEvent) {
        this.#eventObject = event;
    }

    static #isDataLayerReady(): boolean {
        return Array.isArray(window.dataLayer) && window.dataLayer.length > 0;
    }

    static #sendToDataLayer(event: GTMEvent, extraKeys: Record<string, unknown> = {}) {
        if (GoogleTagManager.#isDataLayerReady()) {
            window.dataLayer?.push({ ...event, ...extraKeys, event: 'web_event' });
        }
    }

    // A container without dataLayer.name still counts, so an unexpected GTM shape waits rather than races
    static #getDataLayerContainerIds(): string[] {
        return Object.entries(window.google_tag_manager ?? {})
            .filter(([key, container]) => {
                const dataLayerName = (container as GoogleTagManagerContainer | undefined)
                    ?.dataLayer?.name;
                return (
                    key.startsWith(GTM_CONTAINER_ID_PREFIX) &&
                    (dataLayerName === undefined || dataLayerName === DATA_LAYER_NAME)
                );
            })
            .map(([key]) => key);
    }

    static sendEvent(event: GTMEvent) {
        GoogleTagManager.#sendToDataLayer(event);
    }

    /**
     * Pushes the event, then calls onSettled once every GTM container has finished the tags it
     * fired and minimumMilliseconds have passed, or after maximumMilliseconds, whichever is first.
     * The minimum covers Custom HTML tags, which GTM marks complete before their own request lands.
     */
    static sendEventAndWaitForTags(
        event: GTMEvent,
        onSettled: () => void,
        { minimumMilliseconds, maximumMilliseconds }: TagCompletionWait
    ) {
        const pendingContainerIds = new Set(GoogleTagManager.#getDataLayerContainerIds());
        if (!GoogleTagManager.#isDataLayerReady() || pendingContainerIds.size === 0) {
            GoogleTagManager.#sendToDataLayer(event);
            onSettled();
            return;
        }

        let hasSettled = false;
        let hasMinimumElapsed = false;
        const settle = () => {
            if (!hasSettled) {
                hasSettled = true;
                clearTimeout(minimumTimeoutId);
                clearTimeout(maximumTimeoutId);
                onSettled();
            }
        };
        const settleIfReady = () => {
            if (hasMinimumElapsed && pendingContainerIds.size === 0) {
                settle();
            }
        };

        const minimumTimeoutId = setTimeout(() => {
            hasMinimumElapsed = true;
            settleIfReady();
        }, minimumMilliseconds);
        const maximumTimeoutId = setTimeout(() => {
            // An action, not an error: GTM tags that never report done make this the usual case for now
            LoggingHandler().logUserAction('GTM tags still running at the maximum wait', {
                searchTerm: 'gtm-tags-maximum-wait',
                tags: ['gtm-tracking'],
                fileName: 'googleTagManager.ts',
                additionalData: {
                    eventAction: event.event_action,
                    pendingContainerIds: Array.from(pendingContainerIds),
                    maximumMilliseconds,
                },
            });
            settle();
        }, maximumMilliseconds);

        GoogleTagManager.#sendToDataLayer(event, {
            eventCallback: (containerId: string) => {
                pendingContainerIds.delete(containerId);
                settleIfReady();
            },
        });
    }

    sendEvent(event: GTMEvent) {
        GoogleTagManager.#sendToDataLayer({ ...this.#eventObject, ...event });
    }
}

export { GoogleTagManager };
export type { GTMEvent, incompleteGTMEvent, TagCompletionWait };
